import OneSchemaImporter from '@oneschema/react'
import { alert } from 'actions/flash'
import {
  useGetCustomerImportOneSchemaConfigQuery,
  useSubmitCustomerImportMutation,
} from 'api/userLists'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  closeImporter,
  selectImporterListId,
  selectImporterOpen,
  selectImportMode,
  setActiveImportFileUploadId,
} from 'slices/userLists'
import { getCurrentEnvironment } from 'utilities/urlUtils'
import useCustomerImportRowLimit from './useCustomerImportRowLimit'

const t = buildTranslate('user_lists.import_history.importer')

const CustomerImportImporter: React.FC = () => {
  const dispatch = useDispatch()
  const isOpen = useSelector(state => selectImporterOpen(state as any))
  const listId = useSelector(state => selectImporterListId(state as any))
  const importMode = useSelector(state => selectImportMode(state as any))
  const [isOneSchemaReady, setIsOneSchemaReady] = useState(false)
  const importMaxRowLimit = useCustomerImportRowLimit()

  const {
    data: config,
    isLoading: isConfigLoading,
    isError: isConfigError,
    error: configError,
  } = useGetCustomerImportOneSchemaConfigQuery(undefined, {
    skip: !isOpen,
  })

  const [submitImport] = useSubmitCustomerImportMutation()

  useEffect(() => {
    if (isConfigError) {
      const apiMessage = (configError as any)?.data?.errors?.messages?.[0]
      dispatch(
        alert({
          key: 'danger',
          message: apiMessage || t('error'),
          timeout: 5,
        })
      )
      setIsOneSchemaReady(false)
      dispatch(closeImporter())
    }
  }, [isConfigError, configError, dispatch])

  const handleClose = () => {
    setIsOneSchemaReady(false)
    dispatch(closeImporter())
  }

  // OneSchema's `onSuccess` payload is typed as `any` in @oneschema/react.
  // Per the OneSchema docs the original uploaded filename is nested
  // under `sheet_metadata.original_file_name` — we forward it as
  // `original_filename` so the backend can derive a human-readable
  // display_filename (DATA-5059). Optional chain + type guard handle
  // payload shapes from older OneSchema versions; when the field is
  // missing the backend falls back to its `customer-import` slug.
  const handleSuccess = async (data: any) => {
    const { embed_id, sheet_id } = data
    const candidate = data?.sheet_metadata?.original_file_name
    const original_filename: string | undefined =
      typeof candidate === 'string' && candidate.length > 0
        ? candidate
        : undefined
    if (!listId) {
      dispatch(alert({ key: 'danger', message: t('error'), timeout: 5 }))
      setIsOneSchemaReady(false)
      dispatch(closeImporter())
      return
    }

    try {
      const result = await submitImport({
        embed_id,
        sheet_id,
        user_list_id: listId,
        replace_lists: importMode === 'replace',
        original_filename,
      }).unwrap()

      dispatch(setActiveImportFileUploadId(result.file_upload.id))
      dispatch(
        alert({
          key: 'info',
          message: t('importing'),
          timeout: 5,
        })
      )
      setIsOneSchemaReady(false)
      dispatch(closeImporter())
    } catch (err: any) {
      const apiMessage = err?.data?.errors?.messages?.[0]
      dispatch(
        alert({
          key: 'danger',
          message: apiMessage || t('error'),
          timeout: 5,
        })
      )
      setIsOneSchemaReady(false)
      dispatch(closeImporter())
    }
  }

  const handleCancel = () => {
    setIsOneSchemaReady(false)
    dispatch(closeImporter())
  }

  const handleLaunched = () => {
    setIsOneSchemaReady(true)
  }

  const handleError = (error: { message: string }) => {
    dispatch(
      alert({
        key: 'danger',
        message: error.message || t('error'),
        timeout: 5,
      })
    )
  }

  if (!isOpen) return null

  const showOverlay = isConfigLoading || !config || !isOneSchemaReady

  return (
    <>
      {showOverlay && (
        <div
          className="position-fixed top-0 start-0 vw-100 vh-100 d-flex align-items-center justify-content-center"
          style={{ zIndex: 1001, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <Spinner isLoading size="4x" />
        </div>
      )}
      {config && (
        <OneSchemaImporter
          isOpen={isOpen}
          clientId={config.client_id}
          userJwt={config.jwt}
          templateKey={config.template_key}
          templateOverrides={config.template_overrides}
          importConfig={{ type: 'local', metadataOnly: true }}
          customizationOverrides={{ importMaxRowLimit }}
          inline={false}
          devMode={getCurrentEnvironment() === 'development'}
          className="customer-import-importer position-fixed top-0 start-0 vw-100 vh-100"
          style={{ zIndex: 1000 }}
          onRequestClose={handleClose}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          onError={handleError}
          onLaunched={handleLaunched}
        />
      )}
    </>
  )
}

export default CustomerImportImporter
